import React, { useState, useEffect, Component } from 'react';
import { api } from './api';
import Sidebar from './components/Sidebar';
import CompaniesListPanel from './components/CompaniesListPanel';
import SignalsPanel from './components/SignalsPanel';
import SourcesPanel from './components/SourcesPanel';
import DealsPanel from './components/DealsPanel';
// NurturePanel retired — the `nurture` deal stage in DealsPanel replaces it.
import TasksPanel from './components/TasksPanel';
import ReportingPanel from './components/ReportingPanel';
import AdminSettingsPanel from './components/AdminSettingsPanel';
import LeadsTable from './components/LeadsTable';
import JobQueue from './components/JobQueue';

// Error boundary — catches React render crashes and shows recovery UI
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    console.error('Panel crashed:', error, info?.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 p-8">
          <div className="text-red-400 text-sm font-medium">Something went wrong</div>
          <div className="text-xs text-slate-500 max-w-md text-center">
            {this.state.error?.message || 'An unexpected error occurred.'}
          </div>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 rounded-md text-sm transition-colors"
          >
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function App() {
  const [activePanel, setActivePanel] = useState('companies');
  const [stats, setStats] = useState({
    sources: 0,
    leads: 0,
  });
  const [refreshKey, setRefreshKey] = useState(0);

  // Initialize WebSocket connection
  useEffect(() => {
    api.connectWS();
    return () => {
      if (api.ws) {
        api.ws.close();
      }
    };
  }, []);

  // Load stats
  useEffect(() => {
    loadStats();
    const interval = setInterval(loadStats, 10000);
    return () => clearInterval(interval);
  }, [refreshKey]);

  const loadStats = async () => {
    try {
      const [sourcesData, leadsData, dncData] = await Promise.all([
        api.getSources(),
        api.getLeads({ limit: 1, offset: 0 }),
        api.listDncLeads({ limit: 1, offset: 0 }).catch(() => ({ total: 0 })),
      ]);

      setStats({
        sources: sourcesData?.length || 0,
        leads: leadsData?.total || 0,
        dnc: dncData?.total || 0,
      });
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  };

  const handleRefresh = () => {
    setRefreshKey(k => k + 1);
  };

  const renderPanel = () => {
    switch (activePanel) {
      case 'companies':
        return <CompaniesListPanel key={refreshKey} />;
      case 'leads':
        return (
          <div className="h-full overflow-y-auto scrollable-content p-8 max-w-[1600px] w-full">
            <h1 className="text-xl font-semibold text-white mb-1">Leads</h1>
            <div className="text-slate-500 text-xs mb-4">
              Every person record in Siphon. Filter by company, source, or ICP score.
            </div>
            <LeadsTable key={refreshKey} onRefresh={handleRefresh} />
          </div>
        );
      case 'deals':
        return <DealsPanel key={refreshKey} />;
      case 'tasks':
        return <TasksPanel key={refreshKey} />;
      case 'signals':
        return <SignalsPanel key={refreshKey} />;
      case 'sources':
        return <SourcesPanel key={refreshKey} onRefresh={handleRefresh} />;
      case 'reporting':
        return <ReportingPanel key={refreshKey} />;
      case 'settings':
        return <AdminSettingsPanel key={refreshKey} />;
      default:
        return <CompaniesListPanel key={refreshKey} />;
    }
  };

  return (
    <div className="flex h-screen bg-slate-900 text-slate-100">
      {/* Sidebar */}
      <Sidebar
        activePanel={activePanel}
        setActivePanel={setActivePanel}
        stats={stats}
      />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Panel — wrapped in error boundary so crashes don't kill the app */}
        <div className="flex-1 overflow-hidden bg-slate-900">
          <ErrorBoundary key={activePanel + '-' + refreshKey}>
            {renderPanel()}
          </ErrorBoundary>
        </div>

        {/* Job Queue (bottom sticky bar) */}
        <JobQueue />
      </div>
    </div>
  );
}
